/**
 * Date-only (calendar date) handling — R2-BR-D17 fix. Web twin of
 * packages/api/src/lib/dateOnly.ts; the two packages share no code, so
 * the same rules are implemented here and pinned by parallel tests:
 *
 *  1. A date-only value is a calendar date, "2026-11-15". It is stored
 *     as the instant at UTC midnight of that date and ALWAYS read back
 *     as the UTC calendar date of the stored instant — never through
 *     the viewer's timezone. (Reading a UTC-midnight award date through
 *     a US timezone is what rendered an entered Oct 24 as Oct 23.)
 *  2. A genuinely TIMED instant carries an explicit IANA timezone
 *     (Opportunity.responseDeadlineTz). Its calendar date is read in
 *     THAT timezone: 2026-11-15T19:00:00-05:00 is Nov 15, even though
 *     its UTC date is Nov 16.
 *  3. Writers send date-only fields as the raw "YYYY-MM-DD" string from
 *     the date input (see `dateOnlyInputToIso`), never as a locally
 *     constructed Date — a local-midnight or end-of-local-day instant
 *     has a different UTC date than the date the user picked.
 *  4. Day differences are computed on calendar dates ("today" in the
 *     viewer's presented timezone), never as ceil/floor of an instant
 *     difference — the ceil-vs-floor split is what made the same page
 *     say 53 and 54 days overdue for one deadline.
 */

export interface CalendarDate {
  year: number;
  /** 1–12 */
  month: number;
  /** 1–31 */
  day: number;
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const DAY_MS = 24 * 60 * 60 * 1000;

function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

/** Parse a "YYYY-MM-DD" calendar date; impossible dates return null. */
export function parseCalendarDate(value: string | null | undefined): CalendarDate | null {
  if (!value) return null;
  const m = DATE_ONLY_RE.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  return isRealCalendarDate(year, month, day) ? { year, month, day } : null;
}

export function isValidDateOnlyInput(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && parseCalendarDate(value) !== null;
}

/** "2026-11-15" for a calendar date. */
export function calendarDateToString(cd: CalendarDate): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${cd.year}-${p(cd.month)}-${p(cd.day)}`;
}

/** The canonical stored form of a date-only value: UTC midnight, as ISO. */
export function dateOnlyToIso(value: string | CalendarDate): string | null {
  const cd = typeof value === 'string' ? parseCalendarDate(value) : value;
  if (!cd) return null;
  return new Date(Date.UTC(cd.year, cd.month - 1, cd.day)).toISOString();
}

/**
 * Convert a date input's raw value ("YYYY-MM-DD", exactly what the user
 * picked) to the wire value to send the API. The string is validated and
 * carried as a calendar date — no local Date construction — so the date
 * stored is the date entered, in every timezone.
 */
export function dateOnlyInputToIso(input: string): string | null {
  if (!isValidDateOnlyInput(input)) return null;
  return dateOnlyToIso(input);
}

function zonedParts(d: Date, timeZone: string): CalendarDate | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(d);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
    const year = get('year');
    const month = get('month');
    const day = get('day');
    if (!year || !month || !day) return null;
    return { year, month, day };
  } catch {
    return null;
  }
}

export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * The calendar date a stored value represents: in its explicit timezone
 * when one is supplied (rule 2), else the UTC calendar date (rule 1).
 * A bare "YYYY-MM-DD" string is itself the calendar date.
 */
export function calendarDateOf(
  value: Date | string | null | undefined,
  timeZone?: string | null,
): CalendarDate | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return parseCalendarDate(value);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  if (timeZone) {
    const zoned = zonedParts(d, timeZone);
    if (zoned) return zoned;
  }
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** "YYYY-MM-DD" for a `<input type="date">` value — the stored calendar date. */
export function toDateInputValue(
  value: Date | string | null | undefined,
  timeZone?: string | null,
): string {
  const cd = calendarDateOf(value, timeZone);
  return cd ? calendarDateToString(cd) : '';
}

/** Whole calendar days from `from` to `to` (positive = `to` is later). */
export function calendarDayDiff(from: CalendarDate, to: CalendarDate): number {
  return Math.round(
    (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) / DAY_MS,
  );
}

/**
 * Whole calendar days from today until the given value. "Today" is the
 * viewer's calendar date (or the date in the value's timezone when one
 * is supplied). A deadline dated today is 0 — due today, not overdue —
 * at any time of day.
 */
export function daysUntilCalendar(
  value: Date | string | null | undefined,
  now: Date = new Date(),
  timeZone?: string | null,
): number | null {
  const target = calendarDateOf(value, timeZone);
  if (!target) return null;
  const today = calendarDateOf(now, timeZone ?? localTimeZone());
  if (!today) return null;
  return calendarDayDiff(today, target);
}

/** True when the entered date-only value is before today (viewer's calendar). */
export function isPastCalendarDate(input: string, now: Date = new Date()): boolean {
  const cd = parseCalendarDate(input);
  if (!cd) return false;
  const today = calendarDateOf(now, localTimeZone());
  if (!today) return false;
  return calendarDayDiff(today, cd) < 0;
}

/**
 * Render a date-only value for a person, e.g. "Nov 15, 2026" ('short')
 * or "November 15, 2026" ('long'). No time is ever invented, and the
 * viewer's timezone cannot shift the day.
 */
export function formatDateOnly(
  value: Date | string | null | undefined,
  opts?: { timeZone?: string | null; style?: 'long' | 'short' | 'iso' },
): string | null {
  const cd = calendarDateOf(value, opts?.timeZone);
  if (!cd) return null;
  if (opts?.style === 'iso') return calendarDateToString(cd);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: opts?.style === 'long' ? 'long' : 'short',
    day: 'numeric',
  }).format(new Date(Date.UTC(cd.year, cd.month - 1, cd.day)));
}

/** Long plain-words rendering of a date input's value, for readback. */
export function describeDateInput(input: string): string | null {
  if (!isValidDateOnlyInput(input)) return null;
  return formatDateOnly(input, { style: 'long' });
}
